import { type Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { makeAutoObservable, observable } from "mobx";

import { type Config } from "config/config";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import { type StripeIntegrationClient } from "services/stripeIntegration/stripeIntegrationClient";

type SetupIntentState = "idle" | "loading" | "ready" | "error";

export class SetupIntentStore {
  private _state: SetupIntentState = "idle";
  private _stripe: Stripe | null = null;
  private _clientSecret: string | null = null;
  private _inFlight: Promise<void> | null = null;

  constructor(
    private readonly _stripeIntegrationClient: StripeIntegrationClient,
    private readonly _analyticsManager: AnalyticsManager,
    private readonly _config: Config,
  ) {
    makeAutoObservable(this, {
      _stripe: observable.ref,
      _inFlight: false,
      _stripeIntegrationClient: false,
      _analyticsManager: false,
      _config: false,
    } as any);
  }

  get state(): SetupIntentState {
    return this._state;
  }

  get stripe(): Stripe | null {
    return this._stripe;
  }

  get clientSecret(): string | null {
    return this._clientSecret;
  }

  async init(): Promise<void> {
    if (this._state === "ready" || this._state === "error") {
      return;
    }
    if (this._inFlight) {
      this._analyticsManager.event("setup-intent.deduped");
      await this._inFlight;
      return;
    }

    this._state = "loading";
    this._inFlight = this._run();

    try {
      await this._inFlight;
    } finally {
      this._inFlight = null;
    }
  }

  async retry(): Promise<void> {
    if (this._inFlight) {
      await this._inFlight;
      return;
    }
    this._state = "idle";
    this._stripe = null;
    this._clientSecret = null;
    await this.init();
  }

  private async _run(): Promise<void> {
    try {
      const stripe = await loadStripe(this._config.stripeApiKey);

      if (!stripe) {
        console.error(new Error("Stripe was not initialized"));
        this._state = "error";
        this._analyticsManager.event("setup-intent.error", { error: "stripe-not-initialized" });
        return;
      }

      const paymentMethod = await this._stripeIntegrationClient.beginSetupPaymentMethod();

      this._stripe = stripe;
      this._clientSecret = paymentMethod.clientSecret;
      this._state = "ready";
    } catch (e) {
      this._state = "error";
      this._analyticsManager.event("setup-intent.error", { error: e instanceof Error ? e.message : String(e) });
      console.error(e);
    }
  }
}
